import React, { useMemo } from 'react';

/**
 * @see https://github.com/mui-org/material-ui/blob/651f32beec12e80dbf8d038057d5714720b0a97f/packages/material-ui-utils/src/setRef.ts
 */
export const setRef = <T>(
  ref: React.MutableRefObject<T | null> | ((instance: T | null) => void) | null | undefined,
  value: T | null
): void => {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    // eslint-disable-next-line no-param-reassign
    ref.current = value;
  }
};

/**
 * @see https://github.com/mui-org/material-ui/blob/next/packages/material-ui-utils/src/useForkRef.ts
 */
const useForkRef = <T>(
  refA: React.Ref<T> | null | undefined,
  refB: React.Ref<T> | null | undefined
): React.Ref<T> | null => {
  /**
   * This will create a new function if the ref props change and are defined.
   * This means react will call the old forkRef with `null` and the new forkRef
   * with the ref. Cleanup naturally emerges from this behavior.
   */
  return useMemo(() => {
    if (refA == null && refB == null) {
      return null;
    }
    return refValue => {
      setRef(refA, refValue);
      setRef(refB, refValue);
    };
  }, [refA, refB]);
};

export default useForkRef;
